/**
 * ─────────────────────────────────────────────────────────────────────────────
 * Command Palette — Premium full-screen search for Apunte Digital
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Architecture decisions:
 *
 * 1. ALWAYS in the DOM — the palette is never mounted/unmounted. Visibility is
 *    controlled with CSS `opacity` + `pointer-events` + `visibility`. This
 *    eliminates the black flash that occurred when SolidJS <Show> caused a DOM
 *    insertion near the `blur(60px)` filter in AppBackground, forcing the
 *    browser's GPU compositor to rebuild all layers for that frame.
 *
 * 2. `position: fixed; z-index: 300` — creates its own compositor layer,
 *    completely independent from AppBackground's blur layers.
 *
 * 3. CSS-only transitions — no solid-transition-group, no Deferred. Smooth
 *    animations that never block the paint pipeline.
 *
 * 4. Keyboard-first — full arrow navigation, Enter to select, Escape to close,
 *    Tab cycles through filter chips.
 *
 * 5. Recent searches — persisted in localStorage, shown when input is empty.
 */

import { msg } from "@lingui/core/macro";
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  Match,
  on,
  onCleanup,
  onMount,
  Show,
  Switch,
} from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import { ArrowRight, Clock, FileText, LayoutDashboard, Search, X, Zap } from "@fleet/icons";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { and, block, eq, page, sql } from "@apunte-digital/powersync";
import { createPowerSyncQuery } from "@apunte-digital/shared/solid";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { cn, livePageFilters, queryKeys } from "@apunte-digital/shared";
import { useI18n } from "@/providers/i18n-provider";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { useCommandHandler, useScope } from "@fleet/shortcuts";
import { useBackDismiss } from "@/lib/use-back-dismiss";
import { loadRecent, removeRecentTerm, saveRecent } from "./command-palette-recent";
import { ResultItem } from "./command-palette-result-item";
import {
  FILTER_TYPES,
  type FilterType,
  type QuickAction,
  type SearchResult,
} from "./command-palette.types";

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;
const EXIT_ANIMATION_MS = 200;
const FOCUS_MID_TRANSITION_MS = 50;

// Search reducer clears overlay search params (palette/q/scope) inherited from
// the `_app` layout while preserving the rest of the query. When navigating to
// an option's own URL, it closes the palette IN THE SAME navigate, without a later
// `close()` that (via the layout's signal→URL effect, resolving `to: "."` against
// the PREVIOUS route) would overwrite the destination and return to the origin.
const closeOverlaySearch = (prev: Record<string, unknown>) => ({
  ...prev,
  palette: undefined,
  q: undefined,
  scope: undefined,
});

// ─── CommandPalette ───────────────────────────────────────────────────────────

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette(props: CommandPaletteProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const navigate = useNavigate();

  // Addressable search state (`?q=&filter=`, inherited from the `_app` layout):
  // while the palette is open, the URL reflects the query and filter, so a copied
  // link reproduces exactly the same search. Idempotent (`replace`), it preserves
  // the remaining search params and is cleared on close (in the layout).
  const urlSearch = useSearch({ strict: false }) as () => { q?: string; scope?: FilterType };

  // ── State ──────────────────────────────────────────────────────────────────
  const [query, setQuery] = createSignal(urlSearch().q ?? "");
  const [debouncedQuery, setDebouncedQuery] = createSignal(urlSearch().q ?? "");
  const [filter, setFilter] = createSignal<FilterType>(urlSearch().scope ?? "all");

  const syncSearchUrl = (q: string, f: FilterType) => {
    if (!props.isOpen) {
      return;
    }
    void navigate({
      to: ".",
      // `to: "."` preserves the current route; the reducer preserves the remaining
      // search params (palette, modal, …) and lets TanStack infer `prev`.
      search: (prev) => ({
        ...prev,
        q: q.trim() ? q.trim() : undefined,
        scope: f === "page" ? ("page" as const) : undefined,
      }),
      replace: true,
    });
  };
  const [activeIndex, setActiveIndex] = createSignal(0);
  const [recent, setRecent] = createSignal<string[]>(loadRecent());

  // ── Refs ───────────────────────────────────────────────────────────────────
  let inputRef: HTMLInputElement | undefined = undefined;
  let scrollRef: HTMLDivElement | undefined = undefined;

  // ── Basic handlers ─────────────────────────────────────────────────────────
  const close = () => {
    props.onClose();
  };

  // Browser back closes the palette instead of navigating away (PWA pattern).
  useBackDismiss(
    () => props.isOpen,
    () => {
      close();
    },
  );

  const handleSelectResult = (item: SearchResult) => {
    const q = query().trim();
    if (q) {
      setRecent(saveRecent(q, recent()));
    }

    void navigate({
      to: "/notes/$pageId",
      params: { pageId: item.id },
      search: closeOverlaySearch,
    });
  };

  const handleRecentClick = (term: string) => {
    setQuery(term);
    inputRef?.focus();
  };

  const removeRecent = (e: MouseEvent, term: string) => {
    e.stopPropagation();
    setRecent((prev) => removeRecentTerm(term, prev));
  };

  const getFilterLabel = (f: FilterType): string => {
    if (f === "all") {
      return t(msg`All`);
    }
    return t(msg`Pages`);
  };

  // ── Debounce ───────────────────────────────────────────────────────────────
  createEffect(() => {
    const q = query();
    const timer = setTimeout(() => setDebouncedQuery(q), DEBOUNCE_MS);
    onCleanup(() => {
      clearTimeout(timer);
    });
  });

  // Reset index when query or filter changes
  createEffect(
    on([debouncedQuery, filter], () => {
      setActiveIndex(0);
    }),
  );

  // Focus input when palette opens
  createEffect(
    on(
      () => props.isOpen,
      (open) => {
        if (open) {
          // On open, seed the query/filter from the URL (deep link `?q=&filter=`).
          const seedQ = urlSearch().q ?? "";
          const seedFilter = urlSearch().scope ?? "all";
          if (seedQ !== query()) {
            setQuery(seedQ);
            setDebouncedQuery(seedQ);
          }
          if (seedFilter !== filter()) {
            setFilter(seedFilter);
          }
          // Focus input on the next frame to allow DOM transitions to initialize
          requestAnimationFrame(() => {
            inputRef?.focus();
            inputRef?.select();
          });

          // Fallback timer for cross-platform paint completion
          const t1 = setTimeout(() => {
            inputRef?.focus();
            inputRef?.select();
          }, FOCUS_MID_TRANSITION_MS);

          onCleanup(() => {
            clearTimeout(t1);
          });
        } else {
          // clear on close after a short delay (after exit animation)
          const t3 = setTimeout(() => {
            setQuery("");
            setFilter("all");
            setActiveIndex(0);
          }, EXIT_ANIMATION_MS);

          onCleanup(() => {
            clearTimeout(t3);
          });
        }
      },
    ),
  );

  // ── Search query ───────────────────────────────────────────────────────────
  const searchResults = createPowerSyncQuery<SearchResult>({
    queryKey: () => queryKeys.search.results(debouncedQuery(), filter()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const q = debouncedQuery().trim();
      if (!dbInstance || q.length < MIN_QUERY_LENGTH) {
        return null;
      }

      const escapedQ = `%${q}%`;
      const limit = 30;

      // Pages by title + pages whose content (block.text) matches.
      // The outer selectDistinct deduplicates a page matched through both paths.
      const pageQ = dbInstance
        .select({
          type: sql<"page">`'page'`.as("type"),
          id: page.id,
          text: page.title,
          icon: page.icon,
        })
        .from(page)
        .where(and(sql`${page.title} LIKE ${escapedQ}`, ...livePageFilters()));

      const blockPageQ = dbInstance
        .select({
          type: sql<"page">`'page'`.as("type"),
          id: page.id,
          text: page.title,
          icon: page.icon,
        })
        .from(block)
        .innerJoin(page, eq(block.page_id, page.id))
        .where(and(sql`${block.text} LIKE ${escapedQ}`, ...livePageFilters()));

      return dbInstance
        .selectDistinct()
        .from(pageQ.unionAll(blockPageQ).as("search_results"))
        .limit(limit);
    },
    enabled: () => debouncedQuery().length >= MIN_QUERY_LENGTH && Boolean(drizzle()),
    placeholderData: keepPreviousData,
    suspense: false,
  });

  // ── Quick actions ──────────────────────────────────────────────────────────
  const quickActions = createMemo<QuickAction[]>(() => [
    {
      id: "goto-dashboard",
      label: t(msg`Go to Dashboard`),
      shortcut: "G H",
      icon: () => <LayoutDashboard class="h-4 w-4" />,
      action: () => {
        void navigate({ to: "/", search: closeOverlaySearch });
      },
    },
    {
      id: "goto-notes",
      label: t(msg`Go to Notes`),
      shortcut: "G N",
      icon: () => <FileText class="h-4 w-4" />,
      action: () => {
        void navigate({ to: "/notes", search: closeOverlaySearch });
      },
    },
  ]);

  // ── Flat navigable items ───────────────────────────────────────────────────
  type NavItem =
    | { kind: "result"; item: SearchResult }
    | { kind: "recent"; term: string }
    | { kind: "action"; action: QuickAction };

  const navItems = createMemo<NavItem[]>(() => {
    const q = query().trim();
    const results = searchResults.data ?? [];

    if (q.length >= MIN_QUERY_LENGTH) {
      return results.map((item): NavItem => ({ kind: "result", item }));
    }

    // Empty state: recent + quick actions
    const recentItems = recent().map(
      (term): NavItem => ({
        kind: "recent",
        term,
      }),
    );
    const actionItems = quickActions().map(
      (action): NavItem => ({
        kind: "action",
        action,
      }),
    );
    return [...recentItems, ...actionItems];
  });

  // ── Navigation helpers ─────────────────────────────────────────────────────
  const clampIndex = (i: number) => Math.max(0, Math.min(i, navItems().length - 1));

  const scrollActiveIntoView = () => {
    if (!scrollRef) {
      return;
    }
    const active = scrollRef.querySelector<HTMLElement>("[data-active='true']");
    active?.scrollIntoView({ block: "nearest" });
  };

  const selectActive = () => {
    const items = navItems();
    const idx = activeIndex();
    const item = items[idx];
    if (!item) {
      return;
    }

    if (item.kind === "result") {
      handleSelectResult(item.item);
    } else if (item.kind === "recent") {
      setQuery(item.term);
    } else {
      item.action.action();
    }
  };

  // ── Keyboard handling ──────────────────────────────────────────────────────
  // Push the `palette` scope; the engine's scope-priority stack ensures that
  // list/global hotkeys do not fire while the palette owns the keyboard.
  useScope("palette", () => props.isOpen);
  useCommandHandler(COMMAND_IDS.PALETTE_CLOSE, () => {
    if (!props.isOpen) {
      return;
    }
    close();
  });
  useCommandHandler(COMMAND_IDS.PALETTE_NEXT, () => {
    if (!props.isOpen) {
      return;
    }
    setActiveIndex((i) => clampIndex(i + 1));
    requestAnimationFrame(scrollActiveIntoView);
  });
  useCommandHandler(COMMAND_IDS.PALETTE_PREV, () => {
    if (!props.isOpen) {
      return;
    }
    setActiveIndex((i) => clampIndex(i - 1));
    requestAnimationFrame(scrollActiveIntoView);
  });
  useCommandHandler(COMMAND_IDS.PALETTE_CONFIRM, () => {
    if (!props.isOpen) {
      return;
    }
    selectActive();
  });
  useCommandHandler(COMMAND_IDS.PALETTE_CYCLE_FILTER, () => {
    if (!props.isOpen) {
      return;
    }
    const next: FilterType = filter() === "all" ? "page" : "all";
    setFilter(next);
    syncSearchUrl(query(), next);
  });

  // Keep input focused while the command palette is open
  onMount(() => {
    const handleFocusOut = () => {
      setTimeout(() => {
        if (!props.isOpen || !inputRef) {
          return;
        }

        const related = document.activeElement;
        const panel = inputRef.closest(".command-palette-panel");
        if (!panel) {
          return;
        }

        if (!related || related === document.body || !panel.contains(related)) {
          inputRef.focus();
        }
      }, 0);
    };

    inputRef?.addEventListener("focusout", handleFocusOut);
    onCleanup(() => {
      inputRef?.removeEventListener("focusout", handleFocusOut);
    });
  });

  // ── Derived UI state ───────────────────────────────────────────────────────
  const hasQuery = () => query().trim().length >= MIN_QUERY_LENGTH;
  const isSearching = () => {
    if (!hasQuery()) {
      return false;
    }
    if (!searchResults.data) {
      return query().trim() !== debouncedQuery().trim() || searchResults.isFetching;
    }
    return searchResults.isFetching && !searchResults.data;
  };
  const results = () => searchResults.data ?? [];
  const hasResults = () => results().length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    /*
     * The outer div is ALWAYS in the DOM — visibility is CSS-only.
     * This avoids compositor layer rebuilds that cause the black flash.
     */
    <div
      class="fixed inset-0 z-[300] flex items-start justify-center pt-[clamp(40px,8vh,120px)] px-4 pb-6 bg-background/40 backdrop-blur-[12px] backdrop-saturate-[1.4] transition-all duration-180 ease-elegant will-change-[opacity,visibility]"
      style={{
        opacity: props.isOpen ? "1" : "0",
        "pointer-events": props.isOpen ? "auto" : "none",
        visibility: props.isOpen ? "visible" : "hidden",
      }}
      onPointerDown={(e) => {
        // Close when clicking the backdrop itself
        if (e.target === e.currentTarget) {
          close();
        }
      }}
    >
      <div
        class="command-palette-panel w-full max-w-[620px] max-h-[min(80vh,700px)] flex flex-col bg-card/92 border border-border/70 rounded-2xl shadow-[0_0_0_1px_color-mix(in_oklch,var(--primary)_8%,transparent),0_8px_32px_-4px_rgba(0,0,0,0.3),0_32px_64px_-8px_rgba(0,0,0,0.2)] overflow-hidden transition-all duration-180 ease-snappy will-change-transform [backface-visibility:hidden]"
        style={{
          transform: props.isOpen
            ? "scale3d(1, 1, 1) translate3d(0, 0, 0)"
            : "scale3d(0.97, 0.97, 1) translate3d(0, -8px, 0)",
        }}
        role="dialog"
        aria-modal="true"
        aria-label={t(msg`Search`)}
      >
        {/* ── Header / Input ──────────────────────────────────────────────── */}
        <div class="flex items-center gap-2.5 py-3.5 px-4 border-b border-border/50 shrink-0">
          <div class="text-muted-foreground flex items-center shrink-0">
            <Search class="h-4.5 w-4.5" />
          </div>

          <input
            ref={setInputRef}
            type="text"
            class="flex-1 bg-transparent border-none outline-none text-[15px] font-normal text-foreground caret-primary min-w-0 tracking-[-0.01em] placeholder:text-muted-foreground/70"
            placeholder={t(msg`Search notes or type a command...`)}
            value={query()}
            onInput={(e) => {
              const val = e.currentTarget.value;
              setQuery(val);
              syncSearchUrl(val, filter());
            }}
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck={false}
          />

          <button
            type="button"
            class="flex items-center justify-center w-5.5 h-5.5 rounded-md text-muted-foreground bg-muted/60 border-none cursor-pointer shrink-0 transition-colors duration-120 ease-out hover:bg-muted hover:text-foreground"
            style={{
              display: query() ? "flex" : "none",
            }}
            onClick={() => {
              setQuery("");
              syncSearchUrl("", filter());
              inputRef?.focus();
            }}
            aria-label={t(msg`Clear search`)}
          >
            <X class="h-3.5 w-3.5" />
          </button>

          <button
            type="button"
            class="flex items-center bg-transparent border-none cursor-pointer shrink-0 p-0"
            onClick={close}
            aria-label={t(msg`Close`)}
          >
            <kbd class="inline-flex items-center py-0.5 px-1.5 rounded-[5px] border border-border/60 bg-muted/50 font-mono text-[10px] font-semibold text-muted-foreground tracking-[0.02em]">
              esc
            </kbd>
          </button>
        </div>

        {/* ── Filter chips (shown while searching) ─────────────────────── */}
        <div
          class="flex items-center gap-1.5 py-2 px-4 border-b border-border/40 shrink-0 bg-muted/20 flex-wrap"
          style={{
            display: hasQuery() ? "flex" : "none",
          }}
        >
          <For each={FILTER_TYPES}>
            {(f) => (
              <button
                type="button"
                class={cn(
                  "inline-flex items-center py-0.75 px-2.5 rounded-full text-[11px] font-semibold tracking-[0.02em] text-muted-foreground bg-muted/50 border border-transparent cursor-pointer transition-all duration-120 ease-out hover:text-foreground hover:bg-muted",
                  filter() === f && "text-primary bg-primary/12 border-primary/25",
                )}
                onClick={() => {
                  setFilter(f);
                  syncSearchUrl(query(), f);
                  inputRef?.focus();
                }}
              >
                {getFilterLabel(f)}
              </button>
            )}
          </For>
          <span class="ml-auto text-[10px] text-muted-foreground/50 flex items-center gap-1 whitespace-nowrap">
            <kbd class="inline-flex items-center py-0.25 px-1.25 rounded border border-border/50 bg-muted/50 font-mono text-[9px] font-bold text-muted-foreground">
              Tab
            </kbd>{" "}
            {t(msg`to switch`)}
          </span>
        </div>

        <div
          ref={setScrollRef}
          class="flex-1 overflow-y-auto p-1.5 scroll-smooth overscroll-contain [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border/60 [&::-webkit-scrollbar-thumb]:rounded-full"
        >
          {/*
           * Both sections are ALWAYS in the DOM — we toggle with `display: none`.
           * Using <Show> here would destroy/recreate the entire DOM tree when
           * hasQuery() changes, forcing the browser to recomposite the
           * backdrop-filter layer (black flash) and potentially reset focus.
           */}

          {/* ── Empty state: recents + quick actions ───────────────────── */}
          <div style={{ display: hasQuery() ? "none" : undefined }}>
            <Show when={recent().length > 0}>
              <div class="flex items-center gap-1.5 pt-2 pb-1 px-2.5 text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground/60">
                <Clock class="h-3 w-3" />
                {t(msg`Recent searches`)}
              </div>
              <For each={recent()}>
                {(term, i) => (
                  <div class="relative">
                    <button
                      type="button"
                      data-active={activeIndex() === i()}
                      class={cn(
                        "peer flex items-center gap-2.5 w-full py-[9px] px-2.5 rounded-[10px] border-none bg-transparent cursor-pointer text-left transition-colors duration-80 ease-out text-foreground hover:bg-[color-mix(in_oklch,var(--accent)_70%,transparent)]",
                        activeIndex() === i() &&
                          "bg-[color-mix(in_oklch,var(--primary)_10%,var(--accent)_60%)] active hover:bg-[color-mix(in_oklch,var(--primary)_10%,var(--accent)_60%)]",
                      )}
                      onClick={() => {
                        handleRecentClick(term);
                      }}
                      onPointerEnter={() => setActiveIndex(i())}
                    >
                      <span class="flex items-center justify-center w-7 h-7 rounded-lg shrink-0 bg-[color-mix(in_oklch,var(--muted)_50%,transparent)] text-[color-mix(in_oklch,var(--muted-foreground)_60%,transparent)] transition-colors duration-80 ease-out">
                        <Clock class="h-3.5 w-3.5" />
                      </span>
                      <span
                        class="flex-1 text-[13.5px] font-[450] truncate tracking-[-0.01em]"
                        style={{ "padding-right": "24px" }}
                      >
                        {term}
                      </span>
                    </button>
                    <button
                      type="button"
                      class="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-4.5 h-4.5 rounded border-none bg-transparent text-muted-foreground cursor-pointer opacity-0 shrink-0 transition-all duration-80 ease-out peer-hover:opacity-60 peer-[.active]:opacity-60 hover:opacity-100! hover:bg-destructive/12 hover:text-destructive"
                      onClick={(e) => {
                        removeRecent(e, term);
                      }}
                      aria-label={t(msg`Remove from recent`)}
                    >
                      <X class="h-3 w-3" />
                    </button>
                  </div>
                )}
              </For>
            </Show>

            <div
              class="flex items-center gap-1.5 pt-2 pb-1 px-2.5 text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground/60"
              style={{ "margin-top": recent().length > 0 ? "4px" : "0" }}
            >
              <Zap class="h-3 w-3" />
              {t(msg`Quick navigation`)}
            </div>
            <For each={quickActions()}>
              {(action, i) => {
                const gi = () => recent().length + i();
                return (
                  <button
                    type="button"
                    data-active={activeIndex() === gi()}
                    class={cn(
                      "flex items-center gap-2.5 w-full py-[9px] px-2.5 rounded-[10px] border-none bg-transparent cursor-pointer text-left transition-colors duration-80 ease-out text-foreground hover:bg-[color-mix(in_oklch,var(--accent)_70%,transparent)]",
                      activeIndex() === gi() &&
                        "bg-[color-mix(in_oklch,var(--primary)_10%,var(--accent)_60%)] active hover:bg-[color-mix(in_oklch,var(--primary)_10%,var(--accent)_60%)]",
                    )}
                    onClick={() => {
                      action.action();
                    }}
                    onPointerEnter={() => setActiveIndex(gi())}
                  >
                    <span class="flex items-center justify-center w-7 h-7 rounded-lg shrink-0 bg-[color-mix(in_oklch,var(--primary)_12%,transparent)] text-[var(--primary)] transition-colors duration-80 ease-out">
                      {action.icon()}
                    </span>
                    <span class="flex-1 text-[13.5px] font-[450] truncate tracking-[-0.01em]">
                      {action.label}
                    </span>
                    <Show when={action.shortcut}>
                      <span class="flex items-center gap-0.75 shrink-0">
                        <For each={action.shortcut?.split(" ") ?? []}>
                          {(k) => (
                            <kbd class="inline-flex items-center py-0.25 px-1.25 rounded border border-border/50 bg-muted/40 font-mono text-[9px] font-bold text-muted-foreground">
                              {k}
                            </kbd>
                          )}
                        </For>
                      </span>
                    </Show>
                    <ArrowRight class="h-3.5 w-3.5 opacity-30 ml-auto shrink-0" />
                  </button>
                );
              }}
            </For>
          </div>

          {/* ── Search results ─────────────────────────────────────────── */}
          <div style={{ display: hasQuery() ? undefined : "none" }}>
            <Switch>
              {/* ── Searching Spinner ─────────────────────────────────────── */}
              <Match when={isSearching()}>
                <div class="flex flex-col items-center justify-center gap-2 py-10 px-6 text-muted-foreground">
                  <div class="w-5 h-5 rounded-full border-2 border-primary/20 border-t-primary animate-[spin_0.6s_linear_infinite] will-change-transform" />
                  <span>{t(msg`Searching...`)}</span>
                </div>
              </Match>

              {/* ── Results List ─────────────────────────────────────────── */}
              <Match when>
                <Show
                  when={hasResults()}
                  fallback={
                    <div class="flex flex-col items-center justify-center gap-2 py-10 px-6 text-muted-foreground">
                      <Search class="h-8 w-8 opacity-20" />
                      <p class="text-sm font-medium">{t(msg`No results found`)}</p>
                      <p class="text-xs opacity-50">
                        {(() => {
                          const q = query();
                          return t(msg`Try searching for "${q}"`);
                        })()}
                      </p>
                    </div>
                  }
                >
                  <For each={results()}>
                    {(item, idx) => (
                      <ResultItem
                        item={item}
                        index={idx()}
                        activeIndex={activeIndex()}
                        globalIndex={navItems().findIndex(
                          (n) => n.kind === "result" && n.item.id === item.id,
                        )}
                        onSelect={() => {
                          handleSelectResult(item);
                        }}
                        onHover={(gi) => setActiveIndex(gi)}
                      />
                    )}
                  </For>
                </Show>
              </Match>
            </Switch>
          </div>
        </div>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <div class="flex items-center gap-4 py-2 px-4 border-t border-border/40 bg-muted/20 shrink-0 flex-wrap">
          <span class="flex items-center gap-1.25 text-[10px] text-muted-foreground/55 whitespace-nowrap">
            <kbd class="inline-flex items-center py-0.25 px-1.25 rounded border border-border/50 bg-muted/50 font-mono text-[9px] font-bold text-muted-foreground">
              ↑↓
            </kbd>{" "}
            {t(msg`navigate`)}
          </span>
          <span class="flex items-center gap-1.25 text-[10px] text-muted-foreground/55 whitespace-nowrap">
            <kbd class="inline-flex items-center py-0.25 px-1.25 rounded border border-border/50 bg-muted/50 font-mono text-[9px] font-bold text-muted-foreground">
              ↵
            </kbd>{" "}
            {t(msg`select`)}
          </span>
          <span class="flex items-center gap-1.25 text-[10px] text-muted-foreground/55 whitespace-nowrap">
            <kbd class="inline-flex items-center py-0.25 px-1.25 rounded border border-border/50 bg-muted/50 font-mono text-[9px] font-bold text-muted-foreground">
              Tab
            </kbd>{" "}
            {t(msg`filter`)}
          </span>
          <span class="flex items-center gap-1.25 text-[10px] text-muted-foreground/55 whitespace-nowrap">
            <kbd class="inline-flex items-center py-0.25 px-1.25 rounded border border-border/50 bg-muted/50 font-mono text-[9px] font-bold text-muted-foreground">
              Esc
            </kbd>{" "}
            {t(msg`close`)}
          </span>
        </div>
      </div>
    </div>
  );

  function setInputRef(el: HTMLInputElement) {
    inputRef = el;
  }
  function setScrollRef(el: HTMLDivElement) {
    scrollRef = el;
  }
}
